import { memo, useMemo, useState } from 'react';

import { isMobile } from 'hooks';
import { formatDate } from 'utils/date';
import { Payout } from 'store/api/payout';
import { Drawer } from 'components/drawer';
import { formatPrice } from 'utils/helpers';

import * as LineStyles from '../line-check/styles';
import { RecordTable, RecordColumn } from '../record-table';
import {
  sumAmounts,
  LineDifference,
  useFitDrawerTop,
  LineDrawerHeader,
  useEscapeToClose,
} from '../line-check';

import { BonusesDrawerProps } from './types';
import { BonusDetailsDrawer } from './details';

// Every column the drawer had; Notes is the flexible column.
const columns: RecordColumn<Payout>[] = [
  {
    key: 'date',
    kind: 'date',
    title: 'Date',
    value: (row) => formatDate(row.date),
  },
  { key: 'notes', kind: 'flex', title: 'Notes', value: (row) => row.notes },
  { key: 'type', title: 'Type', kind: 'nowrap', value: (row) => row.type },
  {
    key: 'employee',
    kind: 'compact',
    title: 'Employee',
    value: (row) => row.employee?.user?.name,
  },
  {
    key: 'amount',
    kind: 'amount',
    title: 'Amount',
    value: (row) => formatPrice(row.amount),
  },
];

export const BonusesDrawer = memo(
  ({
    open,
    onClose,
    bonuses,
    eventName,
    lineAmount,
    title = 'Bonuses',
  }: BonusesDrawerProps) => {
    const mobile = isMobile();
    const { ref, topOffset } = useFitDrawerTop(mobile);
    const [selectedBonus, setSelectedBonus] = useState<Payout>();
    const rows = useMemo(() => bonuses || [], [bonuses]);

    useEscapeToClose(!!open, onClose);

    if (!open) {
      return null;
    }

    return (
      <Drawer elevated hideOverlay opened={true} topOffset={topOffset}>
        <LineStyles.DrawerContent ref={ref}>
          <LineDrawerHeader
            title={title}
            onClose={onClose}
            subtitle={eventName}
          />
          {!selectedBonus && (
            <RecordTable
              rows={rows}
              columns={columns}
              getKey={(row) => row.id}
              total={sumAmounts(rows)}
              onRowClick={setSelectedBonus}
              getRowLabel={(row) =>
                `Open ${row.type} ${formatDate(row.date)} ${formatPrice(row.amount)}`
              }
            />
          )}
          {!selectedBonus && (
            <LineDifference lineAmount={lineAmount} total={sumAmounts(rows)} />
          )}

          {selectedBonus && (
            <BonusDetailsDrawer
              id={selectedBonus.id}
              title={selectedBonus.type}
              onClose={() => setSelectedBonus(undefined)}
            />
          )}
        </LineStyles.DrawerContent>
      </Drawer>
    );
  }
);
